import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import { Delete } from "@mui/icons-material";
import { useFetch } from "@/hooks";
import { reviewsApi } from "@/api";
import { useNotification } from "@/context/NotificationContext";
import ReviewCard from "@/components/ReviewCard";
import ConfirmDialog from "@/components/ConfirmDialog";
import LoadingSpinner from "@/components/LoadingSpinner";

export default function Reviews() {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { data, loading, error, refresh } = useFetch(
    () => reviewsApi.getMine(0, 100),
    [],
  );
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await reviewsApi.delete(deleteId);
      notify(t("review.deleteSuccess"), "success");
      refresh();
    } catch {
      notify(t("common.error"), "error");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 3,
        }}
      >
        {t("review.myReviews")}
      </Typography>
      {loading && <LoadingSpinner />}
      {error && <Typography color="error">{error}</Typography>}
      {data?.items.map((review) => (
        <Box
          key={review.id}
          sx={{
            position: "relative",
          }}
        >
          <ReviewCard review={review} showCompany />
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              display: "flex",
              gap: 0.5,
            }}
          >
            <Tooltip title={t("review.delete")}>
              <IconButton size="small" onClick={() => setDeleteId(review.id)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ))}
      {data && data.items.length === 0 && !loading && (
        <Typography
          sx={{
            color: "text.secondary",
            textAlign: "center",
            py: 6,
          }}
        >
          {t("review.noReviews")}
        </Typography>
      )}
      <ConfirmDialog
        open={!!deleteId}
        title={t("review.delete")}
        message={t("review.confirmDelete")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  );
}
